import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

//import images
import OverviewTab from "./OverviewTab";
import ContractsTab from "./ContractsTab";
import AssessmentsTab from "./AssessmentsTab";
import PSEATab from "./PSEATab";
import Header from "./Header";
import Permissions from "Components/Common/Permissions";
import { PSEA_VIEW_CHECKS } from "pages/Assessments/PSEAAssessments/usePseaPermissions";
import { useDispatch, useSelector } from "react-redux";
import { getContractor as onGetContractor } from "slices/thunks";
import { createSelector } from "reselect";
import { useNavigate, useParams } from "react-router-dom";
import { isAllowed } from "common/lib/permissionUtils";
import { useProfile } from "Components/Hooks/UserHooks";

/**
 * Tabs are addressed by slug in the URL (/contractors/:id/:tab) so a reload or
 * a shared link lands on the same tab. `overview` is the default and is served
 * from the bare /contractors/:id path.
 */
const TAB_OVERVIEW = "overview";
const TABS = [
  { slug: TAB_OVERVIEW, label: "Overview" },
  { slug: "contracts", label: "Contracts" },
  { slug: "assessments", label: "Assessments" },
  { slug: "psea", label: "PSEA" },
];

const Content = ({ contractorId }: { contractorId: string }) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { tab } = useParams();
  const { userProfile } = useProfile();

  const canViewPsea = isAllowed(userProfile?.permissions, PSEA_VIEW_CHECKS);

  // Unknown slugs, and PSEA without permission, fall back to the overview.
  const requested = (tab || TAB_OVERVIEW).toLowerCase();
  const isKnown = TABS.some((t) => t.slug === requested);
  const activeTab =
    !isKnown || (requested === "psea" && !canViewPsea)
      ? TAB_OVERVIEW
      : requested;

  const toggleTab = (slug: string) => {
    if (slug === activeTab) return;
    // Overview lives at the bare URL so the common case stays clean.
    navigate(
      slug === TAB_OVERVIEW
        ? `/contractors/${contractorId}`
        : `/contractors/${contractorId}/${slug}`
    );
  };

  // Tidy the address bar when the URL named a tab that cannot be shown.
  useEffect(() => {
    if (tab && activeTab !== requested) {
      navigate(`/contractors/${contractorId}`, { replace: true });
    }
  }, [tab, activeTab, requested, contractorId, navigate]);

  useEffect(() => {
    if (contractorId)
      dispatch(
        onGetContractor({
          id: contractorId,
        })
      );
  }, [contractorId, dispatch]);

  const selectProperties = createSelector(
    (state: any) => state.Contractors,
    (state) => ({
      contractor: state.contractor,
    })
  );
  const { contractor } = useSelector(selectProperties);

  const isDebarred = useMemo(() => {
    return contractor?.performanceRatings?.some(
      (rating: any) =>
        rating?.performanceRatingType?.isDebarred && rating?.isActive
    );
  }, [contractor]);

  const isFlagged = useMemo(() => {
    return contractor?.performanceRatings?.some(
      (rating: any) => rating?.performanceRatingType?.isFlag && rating?.isActive
    );
  }, [contractor]);

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          {contractor?.semgMentions && contractor?.semgMentions?.length > 0 && (
            <Alert color="warning" className="material-shadow">
              <strong> SEMG Mention! </strong> This contractor has been
              mentioned in a SEMG / POE report. Please review the SEMG Mentions
              section below for more information.
            </Alert>
          )}
          {isDebarred && (
            <Alert color="danger" className="material-shadow">
              <strong> Suspended or Debarred! </strong> This contractor has been
              debarred by a UN Agency. Review the performance rating section
              below for more information.
            </Alert>
          )}

          {isFlagged && !isDebarred && (
            <Alert color="warning" className="material-shadow">
              <strong> Flagged! </strong> This contractor has been flagged by a
              UN Agency. Review the performance rating section below for more
              information.
            </Alert>
          )}
          <Card className="">
            <div className="bg-warning-subtle">
              <CardBody className="pb-0 px-4">
                <Header contractor={contractor} />
                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "overview" },
                        "fw-semibold"
                      )}
                      onClick={() => {
                        toggleTab("overview");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "contracts" },
                        "fw-semibold"
                      )}
                      onClick={() => {
                        toggleTab("contracts");
                      }}
                      href="#"
                    >
                      Contracts
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "assessments" },
                        "fw-semibold"
                      )}
                      onClick={() => {
                        toggleTab("assessments");
                      }}
                      href="#"
                    >
                      Assessments
                    </NavLink>
                  </NavItem>
                  <Permissions checks={PSEA_VIEW_CHECKS}>
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "psea" },
                          "fw-semibold"
                        )}
                        onClick={() => {
                          toggleTab("psea");
                        }}
                        href="#"
                      >
                        PSEA
                      </NavLink>
                    </NavItem>
                  </Permissions>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="overview">
              <OverviewTab contractor={contractor} />
            </TabPane>
            <TabPane tabId="contracts">
              <ContractsTab />
            </TabPane>
            <TabPane tabId="assessments">
              <AssessmentsTab />
            </TabPane>
            <Permissions checks={PSEA_VIEW_CHECKS}>
              <TabPane tabId="psea">
                <PSEATab />
              </TabPane>
            </Permissions>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Content;
